/**
 * UITargetController.js
 * High-reliability DOM & Semantic Target Discovery Controller for Kisan Mithr.
 * 
 * Features:
 * - Deterministic field identification (accessible labels, placeholders, names, aria-labels)
 * - React Controlled Component value setter & event triggering
 * - Security gate protecting sensitive credentials (passwords, OTPs, PINs, card numbers)
 * - Page-aware scroll controller for window & internal scroll containers
 * - Button, Tab, Dropdown Select, and Toggle switch control
 * - Non-destructive action verification
 */

/**
 * Checks if a target name or input element represents sensitive credential information.
 */
export const isSensitiveField = (element, targetName = "") => {
  const sensitiveKeywords = [
    "password", "pass", "pwd", "passcode",
    "otp", "pin", "cvv", "card", "cardnumber", "credit", "debit",
    "token", "secret", "auth", "credential"
  ];

  const checkString = (str) => {
    if (!str) return false;
    const s = String(str).toLowerCase().replace(/[\s_-]+/g, "");
    return sensitiveKeywords.some((kw) => s.includes(kw));
  };

  if (targetName && checkString(targetName)) return true;

  if (element) {
    if (element.type === "password") return true;
    if (checkString(element.name)) return true;
    if (checkString(element.id)) return true;
    if (checkString(element.placeholder)) return true;
    if (checkString(element.getAttribute("aria-label"))) return true;
    if (checkString(element.getAttribute("autocomplete"))) return true;

    // Check associated label
    const label = findLabelForElement(element);
    if (label && checkString(label.textContent)) return true;
  }

  return false;
};

/**
 * Finds the associated <label> text for an input/control element.
 */
export const findLabelForElement = (element) => {
  if (!element) return null;

  // 1. Explicit 'for' attribute
  if (element.id) {
    const label = document.querySelector(`label[for="${element.id}"]`);
    if (label) return label;
  }

  // 2. Parent / ancestor <label>
  const parentLabel = element.closest("label");
  if (parentLabel) return parentLabel;

  // 3. Preceding sibling label or text container
  let prev = element.previousElementSibling;
  while (prev) {
    if (prev.tagName.toLowerCase() === "label") return prev;
    if (prev.classList.contains("label") || prev.getAttribute("role") === "label") return prev;
    prev = prev.previousElementSibling;
  }

  return null;
};

/**
 * Checks if a DOM element is visible and interactive in the viewport.
 */
export const isElementVisible = (element) => {
  if (!element) return false;
  if (element.disabled) return false;

  const style = window.getComputedStyle(element);
  if (style.display === "none" || style.visibility === "hidden" || style.opacity === "0") {
    return false;
  }

  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

/**
 * Resolves a semantic field synonym to candidate keywords.
 */
const getFieldKeywords = (target) => {
  const norm = target.toLowerCase().trim();
  const map = {
    name: ["name", "fullname", "full name", "farmer name", "your name", "username", "పేరు", "नाम"],
    email: ["email", "e-mail", "mail", "email address", "ఈమెయిల్", "ईमेल"],
    phone: ["phone", "mobile", "contact number", "phone number", "cell", "ఫోన్", "ఫోన్ నంబర్", "फोन"],
    crop: ["crop", "croptype", "crop type", "commodity", "major crop", "పంట", "ఫసల్", "फसल"],
    location: ["location", "city", "district", "state", "farmlocation", "farm location", "address", "ప్రదేశం", "స్థలం", "स्थान", "शहर"],
    land: ["land", "landsize", "land size", "acres", "acreage", "area", "భూమి", "ఎకరాలు", "जमीन", "एकड़"],
    search: ["search", "find", "query", "lookup", "శోధించు", "వెతుకు", "खोजें"],
    subject: ["subject", "topic", "విషయం", "विषय"],
    message: ["message", "inquiry", "details", "comments", "సందేశం", "సమాచారం", "संदेश"]
  };

  for (const [key, list] of Object.entries(map)) {
    if (norm === key || list.some((term) => norm.includes(term))) {
      return list;
    }
  }

  return [norm];
};

/**
 * Discovers the best matching input or textarea element on the active page.
 */
export const findInputField = (targetName) => {
  if (!targetName) return null;
  const keywords = getFieldKeywords(targetName);

  const inputs = Array.from(document.querySelectorAll("input, textarea")).filter(isElementVisible);
  if (inputs.length === 0) return null;

  // Filter out submit/button/hidden types
  const validInputs = inputs.filter((el) => {
    const type = (el.type || "").toLowerCase();
    return !["submit", "button", "reset", "image", "hidden", "file"].includes(type);
  });

  let bestMatch = null;
  let bestScore = 0;

  for (const el of validInputs) {
    let score = 0;
    const name = (el.name || "").toLowerCase();
    const id = (el.id || "").toLowerCase();
    const placeholder = (el.placeholder || "").toLowerCase();
    const ariaLabel = (el.getAttribute("aria-label") || "").toLowerCase();
    const voiceTarget = (el.getAttribute("data-voice-target") || "").toLowerCase();
    const voiceLabel = (el.getAttribute("data-voice-label") || "").toLowerCase();
    const label = findLabelForElement(el);
    const labelText = (label ? label.textContent : "").toLowerCase();

    for (const kw of keywords) {
      if (voiceTarget === kw || voiceLabel === kw) score += 20;
      if (name === kw) score += 12;
      if (id === kw) score += 10;
      if (name.includes(kw)) score += 6;
      if (id.includes(kw)) score += 5;
      if (labelText.includes(kw)) score += 8;
      if (placeholder.includes(kw)) score += 6;
      if (ariaLabel.includes(kw)) score += 7;

      // Special semantic input type match
      if (kw === "email" && el.type === "email") score += 10;
      if (kw === "search" && el.type === "search") score += 10;
      if ((kw === "phone" || kw === "mobile") && el.type === "tel") score += 10;
      if ((kw === "number" || kw === "size") && el.type === "number") score += 5;
    }

    if (score > bestScore) {
      bestScore = score;
      bestMatch = el;
    }
  }

  // Fallback: If user asked for "search" and there's a search input or text input with search icon
  if (!bestMatch && keywords.includes("search")) {
    const searchInput = validInputs.find((el) => el.type === "search" || el.placeholder.toLowerCase().includes("search"));
    if (searchInput) return searchInput;
  }

  return bestScore >= 4 ? bestMatch : null;
};

/**
 * Updates an input/textarea with React controlled component event triggering.
 */
export const setReactInputValue = (inputElement, value) => {
  if (!inputElement) return false;

  try {
    // 1. Scroll into view and focus
    inputElement.scrollIntoView({ behavior: "smooth", block: "center" });
    inputElement.focus();

    // 2. Use the HTMLInputElement / HTMLTextAreaElement prototype setter
    const prototype = Object.getPrototypeOf(inputElement);
    const descriptor = Object.getOwnPropertyDescriptor(prototype, "value");
    const setter = descriptor ? descriptor.set : null;

    if (setter) {
      setter.call(inputElement, value);
    } else {
      inputElement.value = value;
    }

    // 3. Dispatch synthetic React events with full bubbling
    const inputEvent = new Event("input", { bubbles: true, cancelable: true });
    const changeEvent = new Event("change", { bubbles: true, cancelable: true });

    inputElement.dispatchEvent(inputEvent);
    inputElement.dispatchEvent(changeEvent);

    // 4. Verification
    const isSuccess = String(inputElement.value).trim() === String(value).trim();

    // 5. Visual Cue: brief emerald glow ring highlight for immediate user feedback
    if (isSuccess && typeof window !== "undefined") {
      try {
        const originalTransition = inputElement.style.transition;
        const originalBoxShadow = inputElement.style.boxShadow;
        const originalOutline = inputElement.style.outline;

        inputElement.style.transition = "box-shadow 0.3s ease, outline 0.3s ease";
        inputElement.style.boxShadow = "0 0 0 3px rgba(16, 185, 129, 0.45), 0 0 15px rgba(16, 185, 129, 0.25)";
        inputElement.style.outline = "2px solid #10b981";

        setTimeout(() => {
          try {
            inputElement.style.boxShadow = originalBoxShadow || "";
            inputElement.style.outline = originalOutline || "";
            inputElement.style.transition = originalTransition || "";
          } catch (e) {}
        }, 1400);
      } catch (styleErr) {
        // Non-fatal if styles can't be set
      }
    }

    return isSuccess;
  } catch (err) {
    console.error("[UITargetController] setReactInputValue error:", err);
    return false;
  }
};

/**
 * Clears an identified input field.
 */
export const clearInputField = (targetName) => {
  const field = findInputField(targetName);
  if (!field) return { success: false, reason: "FIELD_NOT_FOUND" };
  if (isSensitiveField(field, targetName)) {
    return { success: false, reason: "SENSITIVE_FIELD_PROTECTION" };
  }

  const ok = setReactInputValue(field, "");
  return { success: ok, reason: ok ? "CLEARED" : "VERIFICATION_FAILED" };
};

/**
 * Focuses an identified input field.
 */
export const focusInputField = (targetName) => {
  const field = findInputField(targetName);
  if (!field) return { success: false, reason: "FIELD_NOT_FOUND" };
  field.scrollIntoView({ behavior: "smooth", block: "center" });
  field.focus();
  return { success: true, reason: "FOCUSED" };
};

/**
 * Finds and activates a visible button.
 */
export const findAndClickButton = (targetName) => {
  if (!targetName) return { success: false, reason: "NO_TARGET" };
  const norm = targetName.toLowerCase().trim();

  // Normalize common action phrases
  const actionSynonyms = {
    submit: ["submit", "send", "save", "send message", "save farm profile", "పంపు", "సమర్పించు", "भेजें", "जमा करें"],
    search: ["search", "find", "search crops", "lookup", "శోధించు", "खोजें"],
    apply: ["apply", "filter", "apply filter", "వర్తించు", "लागू करें"],
    reset: ["reset", "clear", "రిసెట్", "రీసెట్", "रीसेट"],
    close: ["close", "dismiss", "cancel", "ముగించు", "बंद करें"],
    menu: ["menu", "navigation", "open menu", "toggle menu", "మెనూ", "मेनू"]
  };

  let targetKeywords = [norm];
  for (const [actionKey, syns] of Object.entries(actionSynonyms)) {
    if (norm === actionKey || syns.some((s) => norm.includes(s))) {
      targetKeywords = syns;
      break;
    }
  }

  const clickableElements = Array.from(
    document.querySelectorAll("button, input[type='submit'], [role='button'], a")
  ).filter(isElementVisible);

  let bestMatch = null;
  let bestScore = 0;

  for (const el of clickableElements) {
    let score = 0;
    const text = (el.textContent || el.value || "").toLowerCase().trim();
    const ariaLabel = (el.getAttribute("aria-label") || "").toLowerCase();
    const name = (el.getAttribute("name") || "").toLowerCase();
    const id = (el.id || "").toLowerCase();
    const title = (el.title || "").toLowerCase();

    for (const kw of targetKeywords) {
      if (text === kw) score += 20;
      else if (text.startsWith(kw)) score += 14;
      else if (text.includes(kw)) score += 8;

      if (ariaLabel.includes(kw)) score += 10;
      if (name.includes(kw)) score += 6;
      if (id.includes(kw)) score += 5;
      if (title.includes(kw)) score += 7;

      // Type submit boost
      if (kw === "submit" && el.type === "submit") score += 12;
    }

    if (score > bestScore) {
      bestScore = score;
      bestMatch = el;
    }
  }

  if (bestMatch && bestScore >= 6) {
    try {
      bestMatch.scrollIntoView({ behavior: "smooth", block: "center" });

      // Visual Cue: brief click highlight
      if (typeof window !== "undefined") {
        try {
          const origOutline = bestMatch.style.outline;
          bestMatch.style.outline = "2px solid #10b981";
          setTimeout(() => {
            try { bestMatch.style.outline = origOutline || ""; } catch (e) {}
          }, 800);
        } catch (e) {}
      }

      bestMatch.click();
      return { success: true, clickedText: bestMatch.textContent?.trim() || targetName };
    } catch (err) {
      return { success: false, reason: err.message };
    }
  }

  return { success: false, reason: "BUTTON_NOT_FOUND" };
};

/**
 * Finds the primary scrollable container on the active page.
 */
export const getActiveScrollContainer = () => {
  // Check for common scroll containers in Kisan Mithr
  const candidates = [
    document.querySelector(".overflow-y-auto"),
    document.querySelector("main"),
    document.querySelector(".custom-scrollbar"),
    document.scrollingElement || document.documentElement || document.body
  ];

  for (const container of candidates) {
    if (container && container.scrollHeight > container.clientHeight + 10) {
      return container;
    }
  }

  return document.scrollingElement || window;
};

/**
 * Executes controlled page-aware scrolling.
 */
export const performScroll = (direction = "down", amount = "medium") => {
  const container = getActiveScrollContainer();
  const isWindow = container === window || container === document.scrollingElement || container === document.documentElement;

  const currentScrollTop = isWindow ? window.scrollY : container.scrollTop;
  const maxScroll = isWindow
    ? document.documentElement.scrollHeight - window.innerHeight
    : container.scrollHeight - container.clientHeight;

  // Amount in pixels
  let delta = 500;
  if (amount === "small") delta = 250;
  else if (amount === "medium") delta = 600;
  else if (amount === "large") delta = 950;

  let targetTop = currentScrollTop;

  if (direction === "down") {
    targetTop = Math.min(currentScrollTop + delta, maxScroll);
  } else if (direction === "up") {
    targetTop = Math.max(currentScrollTop - delta, 0);
  } else if (direction === "top") {
    targetTop = 0;
  } else if (direction === "bottom") {
    targetTop = maxScroll;
  }

  try {
    if (isWindow) {
      window.scrollTo({ top: targetTop, behavior: "smooth" });
    } else {
      container.scrollTo({ top: targetTop, behavior: "smooth" });
    }

    return {
      success: true,
      direction,
      amount,
      newScrollTop: targetTop
    };
  } catch (err) {
    console.error("[UITargetController] Scroll failed:", err);
    return { success: false, reason: err.message };
  }
};

/**
 * Finds and selects an option in a native <select> or custom dropdown.
 */
export const findAndSelectOption = (targetDropdown, optionValue) => {
  if (!targetDropdown || !optionValue) return { success: false, reason: "MISSING_PARAMS" };

  const selects = Array.from(document.querySelectorAll("select")).filter(isElementVisible);
  const targetNorm = targetDropdown.toLowerCase();
  const optionNorm = optionValue.toLowerCase();

  let targetSelect = null;

  for (const s of selects) {
    const name = (s.name || "").toLowerCase();
    const id = (s.id || "").toLowerCase();
    const label = findLabelForElement(s);
    const labelText = (label ? label.textContent : "").toLowerCase();

    if (name.includes(targetNorm) || id.includes(targetNorm) || labelText.includes(targetNorm)) {
      targetSelect = s;
      break;
    }
  }

  // Fallback: If no name match, check if any select has the option!
  if (!targetSelect) {
    for (const s of selects) {
      const hasOption = Array.from(s.options).some(
        (opt) => opt.text.toLowerCase().includes(optionNorm) || opt.value.toLowerCase().includes(optionNorm)
      );
      if (hasOption) {
        targetSelect = s;
        break;
      }
    }
  }

  if (!targetSelect) {
    return { success: false, reason: "DROPDOWN_NOT_FOUND" };
  }

  // Find matching option
  const matchedOption = Array.from(targetSelect.options).find(
    (opt) => opt.text.toLowerCase().includes(optionNorm) || opt.value.toLowerCase().includes(optionNorm)
  );

  if (!matchedOption) {
    return { success: false, reason: "OPTION_NOT_FOUND" };
  }

  try {
    targetSelect.scrollIntoView({ behavior: "smooth", block: "center" });
    targetSelect.value = matchedOption.value;
    targetSelect.dispatchEvent(new Event("change", { bubbles: true }));
    return { success: true, selectedText: matchedOption.text };
  } catch (err) {
    return { success: false, reason: err.message };
  }
};

/**
 * Finds and toggles a checkbox or switch control (e.g., dark mode, notifications).
 */
export const findAndToggle = (targetName, desiredState) => {
  const norm = (targetName || "").toLowerCase().trim();

  // Find all toggle / checkbox candidates
  const toggles = Array.from(
    document.querySelectorAll('input[type="checkbox"], [role="switch"], [role="checkbox"], button[aria-checked], .toggle, .switch')
  ).filter(isElementVisible);

  if (toggles.length === 0) {
    return { success: false, reason: "NO_TOGGLES_FOUND" };
  }

  let bestToggle = null;
  let bestScore = 0;

  for (const el of toggles) {
    const label = findLabelForElement(el);
    const labelText = (label?.textContent || "").toLowerCase();
    const aria = (el.getAttribute("aria-label") || "").toLowerCase();
    const voice = (el.getAttribute("data-voice-target") || "").toLowerCase();
    const id = (el.id || "").toLowerCase();
    const name = (el.name || "").toLowerCase();

    let score = 0;
    if (labelText.includes(norm) || aria.includes(norm) || voice.includes(norm) || id.includes(norm) || name.includes(norm)) {
      score += 10;
    }
    if (labelText === norm || aria === norm || voice === norm) {
      score += 20;
    }

    if (score > bestScore) {
      bestScore = score;
      bestToggle = el;
    }
  }

  const targetEl = bestToggle || (norm === "this" || !norm ? toggles[0] : null);
  if (!targetEl) {
    return { success: false, reason: "TOGGLE_NOT_FOUND" };
  }

  // Determine current state
  let isCurrentlyChecked = false;
  if (targetEl.tagName.toLowerCase() === "input") {
    isCurrentlyChecked = targetEl.checked;
  } else if (targetEl.getAttribute("aria-checked") !== null) {
    isCurrentlyChecked = targetEl.getAttribute("aria-checked") === "true";
  } else {
    isCurrentlyChecked = targetEl.classList.contains("checked") || targetEl.classList.contains("active");
  }

  // If desiredState is undefined or null, flip the state
  const shouldBeChecked = typeof desiredState === "boolean" ? desiredState : !isCurrentlyChecked;

  if (isCurrentlyChecked === shouldBeChecked) {
    return { success: true, alreadyInState: true, checked: isCurrentlyChecked };
  }

  // Trigger toggle click
  try {
    targetEl.scrollIntoView({ behavior: "smooth", block: "center" });
    targetEl.click();
    return { success: true, checked: shouldBeChecked };
  } catch (err) {
    return { success: false, reason: err.message };
  }
};

/**
 * Finds and switches to a tab by semantic name or index ("next tab", "overview", "details", etc.)
 */
export const findAndSwitchTab = (targetTab) => {
  if (!targetTab) return { success: false, reason: "NO_TARGET" };
  const norm = targetTab.toLowerCase().trim();

  // Find all tab candidates
  const tabs = Array.from(
    document.querySelectorAll("[role='tab'], button[data-tab], .tab-btn, [data-voice-target*='tab']")
  ).filter(isElementVisible);

  if (tabs.length === 0) {
    // Fallback to buttons that look like tab switchers
    const generalButtons = Array.from(document.querySelectorAll("button, a")).filter((el) => {
      if (!isElementVisible(el)) return false;
      const text = (el.textContent || "").toLowerCase().trim();
      const parent = el.parentElement;
      const isTabContainer = parent && (parent.getAttribute("role") === "tablist" || parent.className.includes("tab") || parent.className.includes("nav"));
      return isTabContainer || text.includes(norm);
    });
    tabs.push(...generalButtons);
  }

  if (tabs.length === 0) {
    return { success: false, reason: "NO_TABS_FOUND" };
  }

  // Handle "next tab" or "previous tab"
  if (norm.includes("next")) {
    const currentIndex = tabs.findIndex((t) => t.getAttribute("aria-selected") === "true" || t.classList.contains("active") || t.classList.contains("selected"));
    const nextIndex = (currentIndex + 1) % tabs.length;
    tabs[nextIndex].click();
    return { success: true, tabName: tabs[nextIndex].textContent?.trim() || `Tab ${nextIndex + 1}` };
  }

  // Exact or substring match
  let bestTab = null;
  let bestScore = 0;

  for (const t of tabs) {
    const text = (t.textContent || "").toLowerCase().trim();
    const ariaLabel = (t.getAttribute("aria-label") || "").toLowerCase();
    const voiceTarget = (t.getAttribute("data-voice-target") || "").toLowerCase();

    let score = 0;
    if (text === norm || ariaLabel === norm || voiceTarget === norm) score += 20;
    else if (text.includes(norm) || ariaLabel.includes(norm)) score += 10;

    if (score > bestScore) {
      bestScore = score;
      bestTab = t;
    }
  }

  const targetToClick = bestTab || tabs[0];
  if (targetToClick && bestScore > 0) {
    targetToClick.click();
    return { success: true, tabName: targetToClick.textContent?.trim() || norm };
  }

  return { success: false, reason: "TAB_NOT_FOUND" };
};

/**
 * Controls opening and closing navigation menus with state inspection.
 */
export const controlMenu = (action = "open") => {
  // Check if a mobile menu / drawer / sidebar is currently open
  const openMenuIndicators = [
    document.querySelector("[data-menu-open='true']"),
    document.querySelector(".mobile-menu-open"),
    document.querySelector("[aria-expanded='true'][aria-label*='menu' i]"),
    document.querySelector("[aria-expanded='true'][aria-label*='navigation' i]"),
    document.querySelector("#mobile-menu:not(.hidden)"),
    document.querySelector("aside.translate-x-0")
  ];
  const isAlreadyOpen = openMenuIndicators.some(Boolean);

  if (action === "open" && isAlreadyOpen) {
    return { success: true, alreadyInState: true, isOpen: true, spokenFeedback: "The menu is already open." };
  }
  if (action === "close" && !isAlreadyOpen) {
    return { success: true, alreadyInState: true, isOpen: false, spokenFeedback: "The menu is already closed." };
  }

  // Find menu toggle / close button
  const menuButtons = Array.from(
    document.querySelectorAll("button, [role='button'], a")
  ).filter((el) => {
    if (!isElementVisible(el)) return false;
    const aria = (el.getAttribute("aria-label") || "").toLowerCase();
    const text = (el.textContent || "").toLowerCase().trim();
    const voice = (el.getAttribute("data-voice-target") || "").toLowerCase();
    return (
      aria.includes("menu") ||
      aria.includes("navigation") ||
      aria.includes("drawer") ||
      text.includes("menu") ||
      voice.includes("menu") ||
      el.id.includes("menu") ||
      el.className.includes("hamburger") ||
      el.className.includes("menu-toggle")
    );
  });

  if (menuButtons.length > 0) {
    menuButtons[0].click();
    return {
      success: true,
      isOpen: action === "open",
      spokenFeedback: action === "open" ? "Opening the menu." : "Closing the menu."
    };
  }

  return {
    success: false,
    reason: "MENU_BUTTON_NOT_FOUND",
    spokenFeedback: "I couldn't find the menu control."
  };
};

/**
 * Submits the visible active form on the page safely.
 */
export const submitActiveForm = () => {
  // Find visible submit button first
  const submitButton = Array.from(
    document.querySelectorAll("button[type='submit'], input[type='submit'], [data-voice-target='submit'], button")
  ).find((el) => {
    if (!isElementVisible(el)) return false;
    if (el.type === "submit") return true;
    const text = (el.textContent || "").toLowerCase().trim();
    return text === "submit" || text === "send message" || text === "save" || text === "save profile";
  });

  if (submitButton) {
    submitButton.click();
    return { success: true, clickedText: submitButton.textContent?.trim() || "Submit" };
  }

  // Fallback: trigger submit event on visible form
  const forms = Array.from(document.querySelectorAll("form")).filter(isElementVisible);
  if (forms.length > 0) {
    const activeForm = forms[0];
    activeForm.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
    return { success: true, clickedText: "Form submitted" };
  }

  return { success: false, reason: "NO_ACTIVE_FORM" };
};

/**
 * Page Awareness / Perception Layer:
 * Extracts a lightweight, structured representation of the current page.
 * Strictly local, in-memory, and runs in milliseconds without sending screenshots to AI.
 */
export const getPageInteractionContext = () => {
  if (typeof window === "undefined" || !document) return null;

  const pathname = window.location.pathname || "/";
  const title = document.title || "";

  // Headings
  const headings = Array.from(document.querySelectorAll("h1, h2, h3"))
    .filter(isElementVisible)
    .map((h) => h.textContent?.trim())
    .filter(Boolean)
    .slice(0, 8);

  // Inputs
  const inputs = Array.from(document.querySelectorAll("input, textarea"))
    .filter(isElementVisible)
    .filter((el) => !["hidden", "submit", "button", "image"].includes((el.type || "").toLowerCase()))
    .map((el) => {
      const label = findLabelForElement(el);
      return {
        id: el.id || null,
        name: el.name || null,
        type: el.type || "text",
        label: label ? label.textContent?.trim() : null,
        placeholder: el.placeholder || null,
        value: isSensitiveField(el) ? "[REDACTED]" : el.value || null,
        isSensitive: isSensitiveField(el),
        voiceTarget: el.getAttribute("data-voice-target") || null
      };
    });

  // Selects
  const selects = Array.from(document.querySelectorAll("select"))
    .filter(isElementVisible)
    .map((s) => {
      const label = findLabelForElement(s);
      return {
        id: s.id || null,
        name: s.name || null,
        label: label ? label.textContent?.trim() : null,
        optionsCount: s.options.length,
        selectedValue: s.value || null
      };
    });

  // Buttons
  const buttons = Array.from(document.querySelectorAll("button, input[type='submit'], [role='button']"))
    .filter(isElementVisible)
    .map((b) => ({
      text: b.textContent?.trim() || b.value || null,
      ariaLabel: b.getAttribute("aria-label") || null,
      isSubmit: b.type === "submit",
      disabled: b.disabled || false
    }))
    .slice(0, 15);

  // Scroll Container
  const scrollContainer = getActiveScrollContainer();
  const isScrollable = scrollContainer && (
    scrollContainer === window
      ? document.documentElement.scrollHeight > window.innerHeight
      : scrollContainer.scrollHeight > scrollContainer.clientHeight + 10
  );

  return {
    pathname,
    title,
    headings,
    inputsCount: inputs.length,
    inputs,
    selectsCount: selects.length,
    selects,
    buttonsCount: buttons.length,
    buttons,
    isScrollable
  };
};

const UITargetController = {
  findInputField,
  setReactInputValue,
  clearInputField,
  focusInputField,
  findAndClickButton,
  getActiveScrollContainer,
  performScroll,
  findAndSelectOption,
  findAndToggle,
  findAndSwitchTab,
  controlMenu,
  submitActiveForm,
  getPageInteractionContext,
  isSensitiveField,
  isElementVisible
};

export default UITargetController;
